// Percorso file: src/pages/CoachAttendance.jsx
//
// PRESENZE DEGLI ALLENATORI — cantiere COACH-ATTENDANCE, Fetta 2 (frontend).
//
// Lo strumento con cui l'AMMINISTRATORE segna, per UNA squadra e per le sole
// sessioni GIÀ PASSATE della stagione in corso, se ciascun allenatore c'era o
// no (`present` / `absent`), con una nota facoltativa.
//
// È il calco DICHIARATO di AttendanceSummary.jsx — la vista con cui il coach
// segna i giocatori — adattato da giocatori ad allenatori e da cinque stati a
// due più una nota. Le differenze dal modello, tutte volute:
//   · il cancello è `RequireRole(["ADMIN","SUPERADMIN"])` in App.jsx, non un
//     `isActingAs` interno: la difesa vera sono le tre rotte della Fetta 1
//     (`requireRole("ADMIN","SUPERADMIN")` in backend/server.js);
//   · la squadra SI SCEGLIE IN PAGINA (selettore di barra, pattern di Team
//     Assignment), non arriva dal parametro di rotta;
//   · gli eventi sono SOLO I PASSATI della stagione in corso (vedi
//     `sessioniDellaSquadra`);
//   · la mappa interna delle presenze tiene `{ status, note }`, non il solo
//     stato, perché la modale si precompila su entrambi.
//
// ⚠️ TRE ASSI (due dalla Fetta 4, il terzo dalla Fetta 5). L'interruttore
// «View by» in testa alla pagina sceglie l'asse:
//   · TEAM (la vista di sempre, invariata): una squadra, i suoi allenatori
//     × le sue sessioni — `PresenzeAllenatori`, qui sotto;
//   · COACH (Fetta 4): un allenatore, le sue sessioni di TUTTE le squadre
//     che allena, segnabili — `SegnaturaPerAllenatore`, in coda al file. È
//     l'asse TRASPOSTO della vista per squadra, e il calco DICHIARATO della
//     scheda (CoachAttendanceDetail.jsx, Fetta 3) resa segnabile: stesse
//     letture, stesse due cinture, stessa formula del totale;
//   · REGISTER (Fetta 5): il REGISTRO SETTIMANALE — righe = coach × squadra
//     con almeno una sessione nella settimana mostrata, colonne = i sette
//     giorni (lunedì → domenica), celle segnabili sulle sessioni passate —
//     `RegistroSettimanale`, in coda al file. Per navigazione è il
//     navigatore di MyCalendar.jsx. Nessun totale.
//     Dalla Fetta 6 il registro ha un FILTRO «mostra solo» (squadre e/o
//     coach, in AND) che agisce A VALLE, sulle sole righe mostrate: le
//     righe, le celle e la segnatura sono quelle della Fetta 5, riga per
//     riga — v. `FiltroRegistro` e la nota «IL FILTRO» qui sotto.
//
// ⚠️ IL FILTRO DEL REGISTRO (Fetta 6) VALE PER LA SOLA SESSIONE, di
// proposito. La memoria sul browser è già in uso in casa (`localStorage`,
// forma di `qpscCoachTeamsOrder` in HomeCoach.jsx e `qpscHelpLanguage` in
// enrolAiuto.jsx), ma con una regola scritta a ogni uso: là dentro NON
// finisce nessun identificatore di persona — «nessun uid, nessuna email,
// nessun nome di persona» (HomeCoach.jsx) — e il filtro Coach è fatto di id
// di documento di persone. Inoltre quella chiave è globale per browser, non
// per chi è entrato. Quindi il filtro vive nello STATO del genitore
// (`PresenzeAllenatori`), accanto alla squadra, all'allenatore e alla
// settimana scelti: sopravvive al cambio di settimana e al cambio d'asse,
// e si azzera al ricaricamento della pagina. Nessuna scrittura sul browser.
//
// ⚠️ LE ROTTE, per asse. Asse TEAM, le due della Fetta 1:
//   GET  /coach-attendance/team/:teamName → { coaches, attendance }
//   POST /coach-attendance { eventId, coachId, status, note }
// Asse COACH:
//   GET  /users (nudo, ADMIN/SUPERADMIN)  → l'ELENCO degli allenatori del
//        club, ricavato QUI dalle tessere in proiezione (`allenatoriDelClub`),
//        letto alla PRIMA scelta dell'asse Coach O Register, non al
//        montaggio, e condiviso dai due assi;
//   GET  /users/:coachId                 → il documento dell'allenatore
//        scelto (nome e tessere), come la scheda;
//   GET  /coach-attendance/coach/:coachId → i suoi record (Fetta 1);
//   POST /coach-attendance               → la stessa segnatura.
// Asse REGISTER:
//   GET  /users                          → lo STESSO elenco dell'asse Coach
//        (stesso stato, non si rilegge), con le squadre di ciascuno;
//   GET  /coach-attendance/season        → TUTTI i record dell'anno operativo
//        (Fetta 5, backend), filtrati QUI sugli eventi della settimana;
//   POST /coach-attendance               → la stessa segnatura.
// Le squadre vengono da GET /teams nudo (anno operativo) e gli eventi da
// GET /events nudo, come in tutte le altre pagine — una volta, al montaggio,
// e servono a tutti gli assi (il registro NON rilegge gli eventi a ogni
// settimana: filtra in memoria quelli già letti).
//
// ⚠️ SULL'ASSE TEAM GLI ALLENATORI ARRIVANO GIÀ FILTRATI DAL BACKEND (tessera
// COACH attiva dell'anno che nomina la squadra; «tutte le squadre», PENDING
// e ARCHIVED esclusi): la pagina NON li rifiltra e NON li reintroduce.
// Sull'asse COACH l'elenco lo ricava il client, con LO STESSO METRO (v.
// `allenatoriDelClub`).
//
// ⚠️ LA NOTA SI RIMANDA SEMPRE. Il POST riscrive la nota a ogni segnatura
// (vuota se non mandata, Fetta 1): se l'amministratore cambia il solo stato,
// la modale — precompilata con la nota corrente — la rimanda tal quale.
// Dopo il salvataggio si RILEGGE la GET: nessuno stato ottimistico, come nel
// modello. Dalla Fetta 5 lo stato della modale (cella, stato, nota,
// salvataggio, errore) e le tre mosse (apri, chiudi, salva) vivono in UN
// hook, `useSegnatura`, condiviso dai tre assi: prima era una copia
// dichiarata fra Team e Coach (debito della Fetta 4), e una terza vista ne
// avrebbe fatte tre. Il comportamento dei due assi è quello di prima, riga
// per riga (lo provano le regressioni delle Fette 2 e 4).
//
// ⚠️ LA CHIAVE DI UN ALLENATORE È `coach.id`, l'id del suo DOCUMENTO: è ciò
// che la proiezione restituisce e ciò che `POST /coach-attendance` scrive in
// `coachId` (server.js, `proiezioneAllenatore`). Mai un uid di token.
//
// ⚠️ IL 400 «coachId non allenatore» NON SI MOSTRA nel dettaglio: il corpo
// dell'errore può nominare una persona (Attendance.jsx, `rispondi`). Si dice
// che non ha funzionato, e basta. Il 409 «anno non leggibile» si dice con le
// parole della scheda del socio (UniformDistribution.jsx, `MSG_STAGIONE`).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaRegStickyNote,
  FaTimes
} from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { canonicalRole, normalizeRole } from "../auth/access";
import {
  eDiUnaMiaSquadra,
  squadreDelSoggetto,
  tessereDelSoggetto
} from "../utils/squadre";
import {
  dataEvento,
  evTimeStamp,
  getWeekNumber,
  getWeekRange,
  localKey
} from "../utils/calendario";
// Il navigatore a settimana dell'asse Register (Fetta 5) è quello di
// MyCalendar (§13.3.4: classi `qpsc-cal-weeknav*`), riusato tale e quale —
// il foglio si importa per dichiararne la dipendenza, come
// CoachAttendanceDetail importa questo. In CRA i fogli finiscono comunque
// nel bundle unico.
import "./MyCalendar.css";
import "./CoachAttendance.css";

// Il breakpoint degli strumenti admin (doc 07 §8.1): sopra, la tabella;
// sotto, le card impilate. Scritto a mano di proposito — le media query non
// leggono le custom properties — ed è lo stesso valore di AttendanceSummary
// e Messaging.
const SOGLIA_TABELLA = 768;

// Tetto della nota: lo stesso del backend (`NOTA_PRESENZA_COACH_MAX` =
// `CLUB_NOTE_MAX`, server.js) e dell'area di testo di ClinicEvents.
const NOTA_MAX = 2000;

// I due stati, nell'ordine in cui si mostrano. Le etichette sono quelle che
// l'amministratore legge; il valore è quello che il backend accetta
// (`STATI_PRESENZA_COACH`, server.js). Icone Font Awesome, come nel modello.
const STATI = [
  { valore: "present", etichetta: "Present", Icon: FaCheck },
  { valore: "absent", etichetta: "Absent", Icon: FaTimes }
];

const STATO_DI = Object.fromEntries(STATI.map((s) => [s.valore, s]));

// I tre assi dell'interruttore «View by» (Fetta 4, poi Fetta 5), nell'ordine
// in cui si mostrano: prima la squadra, che è la vista di sempre e il default.
const ASSI = [
  { valore: "team", etichetta: "Team" },
  { valore: "coach", etichetta: "Coach" },
  { valore: "register", etichetta: "Register" }
];

// TESTI A SCHERMO (inglese, CLAUDE.md §3.7). Il testo del 409 è quello della
// scheda del socio e di UniformDistribution, VERBATIM: è lo stesso guasto.
// `MSG_COACH` è quello della scheda (CoachAttendanceDetail.jsx), verbatim;
// `MSG_ALLENATORI` è la sua gemella per l'ELENCO degli allenatori;
// `MSG_REGISTRO` la gemella per i RECORD del registro (Fetta 5).
const MSG_STAGIONE = "Could not read the club season. Please try again in a moment.";
const MSG_ATTESA = "Loading coach attendance…";
const MSG_SQUADRA = "This team's coach attendance is not available.";
const MSG_COACH = "This coach's attendance is not available.";
const MSG_ALLENATORI = "The coach list is not available.";
const MSG_REGISTRO = "The weekly register is not available.";
const MSG_SALVATAGGIO = "Could not save. Please try again.";

// ===========================================================================
// AIUTANTI DI DATA — `dataEvento`, `evTimeStamp` e, per la settimana del
// Register (Fetta 5), `getWeekRange`, `getWeekNumber` e `localKey` dalla sede
// unica utils/calendario.js (giro G-K1)
// ===========================================================================

/** Il tipo dell'evento, col metro del calendario (MyCalendar.jsx). */
const eAllenamento = (ev) => ev && ev.type === "Training";

const uguali = (a, b) => String(a) === String(b);

/** Giorno + data brevi di una `Date` («MON 14/09»): la forma delle card del
 *  calendario. */
function etichettaGiorno(d) {
  return `${d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()} ${d.toLocaleDateString(
    "en-AU",
    { day: "2-digit", month: "2-digit" }
  )}`;
}

/** Giorno + data brevi di un evento, la stessa forma. */
function etichettaBreve(ev) {
  const d = dataEvento(ev);
  if (!d) return String(ev?.date || "");

  return etichettaGiorno(d);
}

/**
 * L'anno della data dell'evento, letto da `dataEvento` così da valere per le
 * DUE grafie (in `DD-MM-YYYY` l'anno sta in coda, non in testa). `null` se la
 * data non si legge: un evento senza data leggibile non è di nessuna stagione.
 */
function annoDellEvento(ev) {
  const d = dataEvento(ev);
  return d ? d.getFullYear() : null;
}

/** Il nome a schermo di una persona (proiezione o documento). Vale per i
 *  due assi: era locale a `PresenzeAllenatori`, dalla Fetta 4 sta qui. */
const nomeDi = (c) => `${c.firstName || ""} ${c.lastName || ""}`.trim();

// ===========================================================================
// GLI ALLENATORI DEL CLUB — dall'elenco di GET /users (asse COACH, Fetta 4)
// ===========================================================================
// Chi entra nel selettore «Coach»: persona ATTIVA al primo livello (status
// assente = ACTIVE, com'è già in proiezione) con ALMENO UNA tessera COACH
// ATTIVA dell'anno operativo. È LO STESSO METRO con cui il backend elenca gli
// allenatori di una squadra (`allenaLaSquadra`, server.js) e con cui la
// scheda ricava le squadre (le due cinture di CoachAttendanceDetail.jsx),
// nella forma già in uso nel client per «i tesserati COACH dell'anno»
// (UniformDistribution.jsx: `tessereDelSoggetto` + `canonicalRole`, così
// MANAGER/TD storici contano). Id, nome e — dalla Fetta 5 — le SQUADRE che
// allena, ricavate dalle tessere in proiezione con le stesse opzioni e la
// stessa cintura di status della scheda (`squadreDelSoggetto`, ruolo COACH,
// `assorbeTutte: false`, doppioni normalizzati; la persona è già filtrata
// ATTIVA qui sopra): servono al registro, che non rilegge nessun documento.
// L'asse Coach continua a rileggere le tessere dal documento alla scelta,
// come fa la scheda, e di `squadre` non si serve. Chi non ha un nome si
// scarta, come le voci senza nome di GET /teams. Ordine per nome, come
// GameCallUps.
//
// ⚠️ Un allenatore con la SOLA tessera «tutte le squadre» ENTRA in elenco
// (ha una tessera COACH attiva) ma, scelto, non ha sessioni: la griglia non
// lo elenca in nessuna squadra e la scheda gli dice «0 of 0». Coerente. Nel
// registro ha `squadre: []` e quindi nessuna riga.
function allenatoriDelClub(utenti, anno) {
  return utenti
    .filter((u) => u && u.id)
    .filter((u) => (normalizeRole(u.status) || "ACTIVE") === "ACTIVE")
    .filter((u) =>
      tessereDelSoggetto(u.contexts, anno).some(
        (ctx) => canonicalRole(ctx?.role) === "COACH"
      )
    )
    .map((u) => ({
      id: String(u.id),
      nome: nomeDi(u),
      squadre: squadreDelSoggetto(u.contexts, anno, {
        ruoli: ["COACH"],
        assorbeTutte: false,
        doppioni: "normalizzati"
      }).nomi
    }))
    .filter((a) => a.nome)
    .sort((a, b) => a.nome.toLowerCase().localeCompare(b.nome.toLowerCase()));
}

// ===========================================================================
// LO STATO DELLA MODALE DI SEGNATURA — un hook, condiviso dai tre assi
// ===========================================================================
// I cinque stati (la casella aperta, lo stato e la nota nuovi, il
// salvataggio in corso, l'errore) e le tre mosse. Era una copia dichiarata
// fra l'asse Team e l'asse Coach (Fetta 4); dalla Fetta 5 vive qui, una
// volta, e i tre assi lo chiamano con la PROPRIA rilettura (`rileggi`:
// `caricaSquadra`, `caricaCoach`, `caricaRegistro`). Corpo e ordine delle
// mosse sono quelli delle due copie, riga per riga:
//   · `salva`: POST /coach-attendance con `coachId` = ID DEL DOCUMENTO;
//     `team` e `date` li prende il backend dall'evento. La nota parte
//     SEMPRE, com'è nella modale: se si è cambiato solo lo stato, è la nota
//     corrente, rimandata tal quale. Poi si chiude e si RILEGGE: nessuno
//     stato ottimistico. Il 400 «coachId non allenatore» porta un `detail`
//     che può nominare una persona: NON si mostra. Solo il 409 ha un testo;
//   · `apri(cella, corrente)`: la casella `{ eventId, coachId, nome, evento }`
//     e il record corrente `{ status, note } | null`, per precompilare;
//   · `chiudi`: non durante un salvataggio.
function useSegnatura(rileggi) {
  const [cella, setCella] = useState(null);
  const [stato, setStato] = useState("present");
  const [nota, setNota] = useState("");
  const [salvataggio, setSalvataggio] = useState(false);
  const [errore, setErrore] = useState("");

  const salva = async () => {
    if (!cella || salvataggio) return;

    setSalvataggio(true);
    setErrore("");

    try {
      await api.post("/coach-attendance", {
        eventId: cella.eventId,
        coachId: cella.coachId,
        status: stato,
        note: nota
      });

      setCella(null);
      await rileggi();
    } catch (err) {
      setErrore(err?.status === 409 ? MSG_STAGIONE : MSG_SALVATAGGIO);
    } finally {
      setSalvataggio(false);
    }
  };

  const apri = (nuova, corrente) => {
    setCella(nuova);
    setStato(corrente?.status || "present");
    setNota(corrente?.note || "");
    setErrore("");
  };

  const chiudi = () => {
    if (salvataggio) return;
    setCella(null);
    setErrore("");
  };

  return { cella, stato, setStato, nota, setNota, salvataggio, errore, salva, apri, chiudi };
}

/** La modale della segnatura, montata solo a casella aperta, cablata
 *  all'hook: è la stessa `ModaleSegnatura` di prima, con gli stessi nomi di
 *  proprietà. */
function FinestraSegnatura({ segnatura }) {
  if (!segnatura.cella) return null;

  return (
    <ModaleSegnatura
      cella={segnatura.cella}
      stato={segnatura.stato}
      onStato={segnatura.setStato}
      nota={segnatura.nota}
      onNota={segnatura.setNota}
      errore={segnatura.errore}
      onAnnulla={segnatura.chiudi}
      onSalva={segnatura.salva}
      salvataggio={segnatura.salvataggio}
    />
  );
}

// ===========================================================================
// IL CANCELLO DI STAGIONE — componente esterno, senza effetti e senza chiamate
// ===========================================================================
// Il cancello di RUOLO sta a monte (RequireRole in App.jsx). Qui si guarda la
// STAGIONE, come UniformDistribution: `seasonStatus` può dire "ready" con
// `operatingSeason` a `null`, quindi si guardano entrambi. Finché non è
// leggibile, nessuna chiamata parte — il componente interno non è montato.
export default function CoachAttendance() {
  const { operatingSeason, seasonStatus } = useAuth() || {};

  const annoOperativo = operatingSeason == null ? null : operatingSeason;
  const stagioneLeggibile = seasonStatus === "ready" && annoOperativo !== null;

  if (!stagioneLeggibile) {
    return (
      <HomeShell backTo="/home" showSwitch={false} width="admin">
        <h1 className="qpsc-page-title">Coach Attendance</h1>
        <p className="qpsc-catt-empty">
          {seasonStatus === "loading" ? MSG_ATTESA : MSG_STAGIONE}
        </p>
      </HomeShell>
    );
  }

  return <PresenzeAllenatori annoOperativo={annoOperativo} />;
}

// ===========================================================================
// LA PAGINA — montata solo a stagione leggibile
// ===========================================================================
function PresenzeAllenatori({ annoOperativo }) {
  const navigate = useNavigate();

  const [teams, setTeams] = useState([]);
  const [squadra, setSquadra] = useState("");
  const [coaches, setCoaches] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [events, setEvents] = useState([]);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState("");

  // Quale schermata si vede sotto la soglia. Con un ASCOLTATORE, non
  // leggendo `window.innerWidth` dentro la resa (pattern di Messaging e
  // AttendanceSummary).
  const [larghezza, setLarghezza] = useState(() =>
    typeof window === "undefined" ? SOGLIA_TABELLA : window.innerWidth
  );

  useEffect(() => {
    const misura = () => setLarghezza(window.innerWidth);

    window.addEventListener("resize", misura);
    return () => window.removeEventListener("resize", misura);
  }, []);

  const compatto = larghezza < SOGLIA_TABELLA;

  // Su telefono si guarda per ALLENATORE o per SESSIONE: pivot lato client
  // degli stessi dati già scaricati, come nel modello.
  const [vista, setVista] = useState("coach");
  const [apertoCoach, setApertoCoach] = useState("");
  const [apertoEvento, setApertoEvento] = useState("");

  // -----------------------------------------------------------------------
  // L'ASSE (Fetta 4, poi 5): per SQUADRA — la vista di sempre, default —,
  // per ALLENATORE, o il REGISTRO settimanale. Le tre scelte (squadra,
  // allenatore, settimana) vivono qui, una accanto all'altra: cambiando
  // asse e tornando, ognuna ritrova la sua.
  // -----------------------------------------------------------------------
  const [asse, setAsse] = useState("team");
  const perSquadra = asse === "team";
  const perCoach = asse === "coach";
  const perRegistro = asse === "register";
  const [coachScelto, setCoachScelto] = useState("");

  // Gli allenatori del club per il selettore «Coach» e per le righe del
  // registro: `null` = non ancora letti. Si leggono alla PRIMA scelta di
  // uno dei due assi e una volta sola, non al montaggio: l'elenco intero
  // del club (GET /users) non serve alla vista per squadra, e non si
  // scarica per niente.
  const [allenatori, setAllenatori] = useState(null);
  const [erroreAllenatori, setErroreAllenatori] = useState("");

  // LA SETTIMANA del registro (Fetta 5): il navigatore di MyCalendar.jsx,
  // riga per riga — si parte da oggi a mezzanotte e ci si sposta di sette
  // giorni per volta, senza confini (i calendari non ne hanno: le celle si
  // accendono solo per gli eventi dell'anno operativo, v. il registro).
  const [settimana, setSettimana] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });

  function settimanaPrecedente() {
    const d = new Date(settimana);
    d.setDate(d.getDate() - 7);
    setSettimana(d);
  }
  function settimanaSuccessiva() {
    const d = new Date(settimana);
    d.setDate(d.getDate() + 7);
    setSettimana(d);
  }

  // Memorizzato perché il lunedì (una `Date`) scende al registro come prop:
  // una `Date` nuova a ogni resa gli farebbe ricalcolare le righe a vuoto.
  const [inizioSettimanaStr, fineSettimanaStr, inizioSettimana] = useMemo(
    () => getWeekRange(settimana),
    [settimana]
  );
  const numeroSettimana = getWeekNumber(settimana);

  // -----------------------------------------------------------------------
  // IL FILTRO DEL REGISTRO (Fetta 6) — «mostra solo» queste squadre e/o
  // questi coach, in AND. Vive QUI, accanto alla settimana, per la stessa
  // ragione: cambiando settimana o asse e tornando, il registro ritrova il
  // suo filtro. SOLA SESSIONE (v. la nota in testa al file): nessuna
  // scrittura sul browser. Le due liste tengono ciò che il registro
  // confronta — i NOMI di squadra così come stanno sulle tessere e gli ID di
  // documento dei coach (`coach.id`, la chiave di sempre). Vuote = tutto.
  // `filtriAperti` è il solo stato di veste: il pannello delle spunte si
  // apre e si chiude, il filtro vale anche a pannello chiuso.
  // -----------------------------------------------------------------------
  const [squadreFiltro, setSquadreFiltro] = useState([]);
  const [coachFiltro, setCoachFiltro] = useState([]);
  const [filtriAperti, setFiltriAperti] = useState(false);

  // Aggiunge o toglie una voce da una lista di stringhe: la forma di
  // `toggleApprovedTeam` (TeamAssignment.jsx), sulle spunte delle squadre.
  const commuta = (lista, voce) =>
    lista.includes(voce) ? lista.filter((v) => v !== voce) : [...lista, voce];

  const azzeraFiltri = () => {
    setSquadreFiltro([]);
    setCoachFiltro([]);
  };

  // COSA SI OFFRE NEL FILTRO — solo ciò che può fare una riga del registro,
  // dall'elenco già letto (`allenatori`, con le `squadre` della Fetta 5):
  // nessuna lettura nuova, nessun catalogo esterno (GET /teams non c'entra:
  // una squadra senza coach non ha righe). I coach: quelli con almeno una
  // squadra — chi ha la sola tessera «tutte le squadre» ha `squadre: []` e
  // nessuna riga, e non si offre. Le squadre: l'unione delle loro, senza
  // doppioni col metro di casa (`eDiUnaMiaSquadra`, normalizzato sui due
  // lati: «U12» e « u12 » sono una voce sola, come nella tendina di User
  // List) e in ordine di nome, come l'elenco dei coach. L'ordine dei coach è
  // già quello di `allenatoriDelClub`.
  const coachOfferti = useMemo(
    () => (allenatori || []).filter((a) => a.squadre.length > 0),
    [allenatori]
  );

  const squadreOfferte = useMemo(() => {
    const nomi = [];
    for (const a of coachOfferti) {
      for (const nome of a.squadre) {
        if (!eDiUnaMiaSquadra(nome, nomi)) nomi.push(nome);
      }
    }
    return nomi.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [coachOfferti]);

  // -----------------------------------------------------------------------
  // LE SQUADRE E GLI EVENTI — una volta, al montaggio. Chiamate NUDE = anno
  // operativo (GET /teams) e calendario intero (GET /events): il filtro di
  // squadra e di tempo si fa qui, come nelle altre pagine.
  // -----------------------------------------------------------------------
  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const dati = await api.get("/teams");
        const elenco = Array.isArray(dati) ? dati : [];
        // Forma di `teamNames` (TeamAssignment.jsx): i nomi, senza i vuoti.
        const nomi = elenco.map((t) => t?.name).filter(Boolean);
        if (vivo) setTeams(nomi);
      } catch {
        if (vivo) setTeams([]);
      }
    })();

    (async () => {
      try {
        const dati = await api.get("/events");
        if (vivo) setEvents(Array.isArray(dati) ? dati : []);
      } catch {
        if (vivo) setEvents([]);
      }
    })();

    return () => {
      vivo = false;
    };
  }, []);

  // -----------------------------------------------------------------------
  // GLI ALLENATORI DEL CLUB — alla prima scelta dell'asse Coach (Fetta 4) o
  // Register (Fetta 5): `!perSquadra` copre entrambi, e l'elenco è UNO.
  // GET /users nudo, il ramo elenco riservato ad ADMIN/SUPERADMIN, letto
  // come lo leggono Team Assignment e Uniform Distribution; il filtro sta
  // in `allenatoriDelClub`. Un guasto dà l'elenco vuoto e la frase gemella
  // di `MSG_SQUADRA`; tornando all'asse Team mentre la lettura è in corso,
  // la risposta si scarta e la prossima scelta di Coach/Register rilegge.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (perSquadra || allenatori !== null) return undefined;

    let vivo = true;

    (async () => {
      try {
        const dati = await api.get("/users");
        if (!vivo) return;

        setAllenatori(allenatoriDelClub(Array.isArray(dati) ? dati : [], annoOperativo));
        setErroreAllenatori("");
      } catch {
        if (!vivo) return;

        setAllenatori([]);
        setErroreAllenatori(MSG_ALLENATORI);
      }
    })();

    return () => {
      vivo = false;
    };
  }, [perSquadra, allenatori, annoOperativo]);

  // -----------------------------------------------------------------------
  // I DATI DELLA SQUADRA SCELTA — la rotta della Fetta 1, una sola.
  // -----------------------------------------------------------------------
  // La squadra di cui una risposta è ancora valida (pattern di
  // `idInCorsoRef`, EditMemberPage.jsx). Se l'amministratore sceglie A e poi
  // B prima che A risponda, e A risponde DOPO B, senza questa guardia
  // resterebbero gli allenatori di A sotto le sessioni di B. La guardia è
  // sulla SQUADRA, non sulla singola chiamata, di proposito: la rilettura
  // dopo un salvataggio (`salvaCella` → `caricaSquadra`) chiede la squadra
  // corrente e va applicata; solo la risposta di una squadra ormai
  // abbandonata si scarta.
  const squadraInCorsoRef = useRef(null);

  const caricaSquadra = useCallback(async () => {
    if (!squadra) {
      setCoaches([]);
      setAttendance([]);
      setErrore("");
      return;
    }

    try {
      const dati = await api.get(
        `/coach-attendance/team/${encodeURIComponent(squadra)}`
      );
      if (squadraInCorsoRef.current !== squadra) return;

      setCoaches(Array.isArray(dati?.coaches) ? dati.coaches : []);
      setAttendance(Array.isArray(dati?.attendance) ? dati.attendance : []);
      setErrore("");
    } catch (err) {
      if (squadraInCorsoRef.current !== squadra) return;

      // Il 409 «stagione illeggibile» si distingue sul solo codice; rifiuto e
      // guasto di rete finiscono nello stesso ramo generico, senza dettaglio.
      setCoaches([]);
      setAttendance([]);
      setErrore(err?.status === 409 ? MSG_STAGIONE : MSG_SQUADRA);
    }
  }, [squadra]);

  useEffect(() => {
    setApertoCoach("");
    setApertoEvento("");

    // Senza squadra non c'è nulla da chiedere: si azzera e basta, in modo
    // sincrono, senza un giro asincrono a vuoto.
    if (!squadra) {
      setCoaches([]);
      setAttendance([]);
      setErrore("");
      setCaricamento(false);
      return undefined;
    }

    let vivo = true;

    squadraInCorsoRef.current = squadra;
    setCaricamento(true);

    caricaSquadra().finally(() => {
      if (vivo) setCaricamento(false);
    });

    return () => {
      vivo = false;
      squadraInCorsoRef.current = null;
    };
  }, [squadra, caricaSquadra]);

  // -----------------------------------------------------------------------
  // LE SESSIONI DA MOSTRARE — della squadra, GIÀ PASSATE, della stagione in
  // corso, dalla più recente.
  //   · squadra: `eDiUnaMiaSquadra` (utils/squadre.js), che normalizza i due
  //     lati ed ESCLUDE la sentinella "all" e gli eventi senza squadra;
  //   · passate: l'INVERSO di Attendance.jsx (`evTimeStamp(ev) >= adesso`
  //     sono i futuri) — confronto locale al browser;
  //   · stagione in corso: l'anno della data dell'evento = anno operativo.
  //     Gli eventi non portano `season` e il backend timbra la presenza con
  //     l'anno CORRENTE qualunque sia la data: senza questo filtro si
  //     segnerebbero sessioni di stagioni vecchie sotto l'anno sbagliato.
  // -----------------------------------------------------------------------
  const sessioni = useMemo(() => {
    if (!squadra) return [];

    const adesso = Date.now();

    return events
      .filter((ev) => eDiUnaMiaSquadra(ev.team, [squadra]))
      .filter((ev) => annoDellEvento(ev) === annoOperativo)
      .filter((ev) => evTimeStamp(ev) < adesso)
      .sort((a, b) => evTimeStamp(b) - evTimeStamp(a));
  }, [events, squadra, annoOperativo]);

  // -----------------------------------------------------------------------
  // LA MATRICE — un solo attraversamento delle presenze (calco di
  // `statistiche`, AttendanceSummary.jsx), con due differenze: la chiave
  // persona è `coachId` e la casella tiene `{ status, note }`.
  // -----------------------------------------------------------------------
  const statistiche = useMemo(() => {
    const perEvento = new Map();
    for (const ev of sessioni) perEvento.set(String(ev.id), true);

    const perAllenatore = new Map();

    for (const voce of attendance) {
      if (!perEvento.has(String(voce.eventId))) continue;

      const chiave = String(voce.coachId);
      if (!perAllenatore.has(chiave)) perAllenatore.set(chiave, new Map());
      perAllenatore.get(chiave).set(String(voce.eventId), {
        status: voce.status,
        note: typeof voce.note === "string" ? voce.note : ""
      });
    }

    const esito = new Map();

    for (const c of coaches) {
      const caselle = perAllenatore.get(String(c.id)) || new Map();

      let present = 0;
      let absent = 0;

      for (const ev of sessioni) {
        const stato = caselle.get(String(ev.id))?.status || null;
        if (stato === "present") present += 1;
        else if (stato === "absent") absent += 1;
      }

      esito.set(String(c.id), {
        caselle,
        present,
        absent,
        percent: sessioni.length ? Math.round((present / sessioni.length) * 100) : 0
      });
    }

    return esito;
  }, [coaches, sessioni, attendance]);

  const vuote = { caselle: new Map(), present: 0, absent: 0, percent: 0 };
  const statsDi = (coachId) => statistiche.get(String(coachId)) || vuote;
  const casellaDi = (coachId, eventId) =>
    statsDi(coachId).caselle.get(String(eventId)) || null;

  // LA SCHEDA DELL'ALLENATORE (Fetta 3): solo l'id nel percorso, come
  // `onProfilo` in AttendanceSummary — niente stato di navigazione, la
  // scheda rilegge tutto da sé e funziona anche per indirizzo diretto.
  const apriScheda = (id) => navigate(`/coach-attendance/coach/${id}`);

  // -----------------------------------------------------------------------
  // LA SEGNATURA — l'hook `useSegnatura` con la rilettura della squadra
  // (`caricaSquadra`): POST /coach-attendance con `coachId` = ID DEL
  // DOCUMENTO, nota sempre rimandata, rilettura dopo. La casella che si
  // apre è { eventId, coachId, nome, evento }, precompilata su stato E nota
  // correnti.
  // -----------------------------------------------------------------------
  const segnatura = useSegnatura(caricaSquadra);

  const apriCella = (coach, ev) =>
    segnatura.apri(
      {
        eventId: ev.id,
        coachId: coach.id,
        nome: nomeDi(coach),
        evento: etichettaBreve(ev)
      },
      casellaDi(coach.id, ev.id)
    );

  // =========================================================================
  // RESA
  // =========================================================================
  return (
    // Colonna degli strumenti admin (§8.1): sopra i 768px tutta la finestra
    // con 24px di margine per lato, sotto la colonna standard — lo fa
    // HomeShell.css, con lo stesso breakpoint.
    <HomeShell backTo="/home" showSwitch={false} width="admin">
      <h1 className="qpsc-page-title">Coach Attendance</h1>

      {/* L'INTERRUTTORE DELL'ASSE (Fetta 4): gruppo-scelta §6 del telaio, lo
          stesso del «Show» qui sotto. Sta SOPRA il selettore, perché decide
          cosa il selettore offre. */}
      <div className="qpsc-choice-group qpsc-catt-axis">
        <span className="qpsc-choice-group__label">View by</span>
        <div className="qpsc-choice-group__options">
          {ASSI.map(({ valore: v, etichetta }) => (
            <button
              key={v}
              type="button"
              className={
                "qpsc-btn-dark" + (asse === v ? " qpsc-choice-option--selected" : "")
              }
              aria-pressed={asse === v}
              onClick={() => setAsse(v)}
            >
              {etichetta}
            </button>
          ))}
        </div>
      </div>

      {/* LA BARRA: il selettore, nella forma dei filtri di Team Assignment
          (qpsc-field + qpsc-select). La prima voce è un SEGNAPOSTO, non
          «All teams»: qui si sceglie una squadra sola — o, sull'asse Coach,
          un allenatore solo. Sull'asse Register il «filtro» è la SETTIMANA:
          il navigatore di MyCalendar (§13.3.4), nella stessa sede. */}
      <div className="qpsc-catt-bar">
        {perRegistro ? (
          <div className="qpsc-cal-weeknav qpsc-catt-weeknav">
            <button
              type="button"
              className="qpsc-cal-weeknav__arrow"
              onClick={settimanaPrecedente}
              aria-label="Previous week"
            >
              <FaChevronLeft />
            </button>

            <div className="qpsc-cal-weeknav__center">
              <span className="qpsc-cal-weeknav__dates">
                {inizioSettimanaStr} &mdash; {fineSettimanaStr}
              </span>
              <span className="qpsc-cal-weeknav__week">Week {numeroSettimana}</span>
            </div>

            <button
              type="button"
              className="qpsc-cal-weeknav__arrow"
              onClick={settimanaSuccessiva}
              aria-label="Next week"
            >
              <FaChevronRight />
            </button>
          </div>
        ) : perSquadra ? (
          <div className="qpsc-field qpsc-catt-field">
            <span className="qpsc-field__label">Team</span>
            <select
              className={
                "qpsc-select" + (squadra ? "" : " qpsc-select--placeholder")
              }
              aria-label="Team"
              value={squadra}
              onChange={(e) => setSquadra(e.target.value)}
            >
              <option value="">Select a team…</option>
              {teams.map((nome) => (
                <option key={nome} value={nome}>{nome}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="qpsc-field qpsc-catt-field">
            <span className="qpsc-field__label">Coach</span>
            <select
              className={
                "qpsc-select" + (coachScelto ? "" : " qpsc-select--placeholder")
              }
              aria-label="Coach"
              value={coachScelto}
              onChange={(e) => setCoachScelto(e.target.value)}
              disabled={allenatori === null}
            >
              <option value="">
                {allenatori === null ? "Loading coaches…" : "Select a coach…"}
              </option>
              {(allenatori || []).map((a) => (
                <option key={a.id} value={a.id}>{a.nome}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* GLI ASSI COACH (Fetta 4) E REGISTER (Fetta 5): tutto ciò che sta
          sotto la barra è dei due componenti in coda al file; qui solo il
          guasto dell'elenco e il club senza allenatori, comuni ai due. */}
      {!perSquadra && erroreAllenatori && (
        <p className="qpsc-catt-error">{erroreAllenatori}</p>
      )}

      {!perSquadra && !erroreAllenatori && allenatori && allenatori.length === 0 && (
        <p className="qpsc-catt-empty">No coaches this season.</p>
      )}

      {perCoach && !erroreAllenatori && (allenatori === null || allenatori.length > 0) && (
        <SegnaturaPerAllenatore
          idCoach={coachScelto}
          annoOperativo={annoOperativo}
          events={events}
          compatto={compatto}
        />
      )}

      {/* IL FILTRO DEL REGISTRO (Fetta 6): SOLO sull'asse Register, sotto la
          barra della settimana, e solo quando c'è qualcosa da filtrare
          (elenco letto, non guasto, almeno un coach con una squadra). Stato
          nel genitore, resa in `FiltroRegistro`. */}
      {perRegistro && !erroreAllenatori && coachOfferti.length > 0 && (
        <FiltroRegistro
          squadre={squadreOfferte}
          coach={coachOfferti}
          squadreScelte={squadreFiltro}
          coachScelti={coachFiltro}
          onSquadra={(nome) => setSquadreFiltro((lista) => commuta(lista, nome))}
          onCoach={(id) => setCoachFiltro((lista) => commuta(lista, id))}
          onAzzera={azzeraFiltri}
          aperto={filtriAperti}
          onApri={() => setFiltriAperti((a) => !a)}
        />
      )}

      {perRegistro && !erroreAllenatori && (allenatori === null || allenatori.length > 0) && (
        <RegistroSettimanale
          allenatori={allenatori}
          annoOperativo={annoOperativo}
          events={events}
          inizioSettimana={inizioSettimana}
          compatto={compatto}
          squadreFiltro={squadreFiltro}
          coachFiltro={coachFiltro}
        />
      )}

      {perSquadra && squadra && !caricamento && !errore && (
        <div className="qpsc-catt-context">
          <span className="qpsc-catt-context__team">{squadra}</span>
          <span className="qpsc-catt-context__meta">
            Season {annoOperativo} &middot; {sessioni.length} past{" "}
            {sessioni.length === 1 ? "session" : "sessions"}
          </span>
        </div>
      )}

      {perSquadra && errore && <p className="qpsc-catt-error">{errore}</p>}

      {/* SU TELEFONO: il bivio fra le due letture degli STESSI dati (gruppo-
          scelta §6 del telaio). Solo se c'è qualcosa da leggere: senza
          sessioni passate resta il solo messaggio del corpo, non un bivio
          sopra un vuoto. */}
      {perSquadra && compatto && squadra && !caricamento && !errore && coaches.length > 0 && sessioni.length > 0 && (
        <div className="qpsc-choice-group">
          <span className="qpsc-choice-group__label">Show</span>
          <div className="qpsc-choice-group__options">
            <button
              type="button"
              className={
                "qpsc-btn-dark" +
                (vista === "coach" ? " qpsc-choice-option--selected" : "")
              }
              aria-pressed={vista === "coach"}
              onClick={() => setVista("coach")}
            >
              By coach
            </button>
            <button
              type="button"
              className={
                "qpsc-btn-dark" +
                (vista === "event" ? " qpsc-choice-option--selected" : "")
              }
              aria-pressed={vista === "event"}
              onClick={() => setVista("event")}
            >
              By session
            </button>
          </div>
        </div>
      )}

      {/* IL CORPO DELL'ASSE TEAM — invariato dalla Fetta 2: sull'asse Coach
          non si rende (il corpo è quello del componente qui sopra). */}
      {!perSquadra ? null : !squadra ? (
        <p className="qpsc-catt-empty">Select a team to see its coaches.</p>
      ) : caricamento ? (
        <p className="qpsc-catt-empty">{MSG_ATTESA}</p>
      ) : errore ? null : coaches.length === 0 ? (
        <p className="qpsc-catt-empty">No coaches in this team this season.</p>
      ) : sessioni.length === 0 ? (
        <p className="qpsc-catt-empty">No past sessions this season.</p>
      ) : compatto ? (
        vista === "coach" ? (
          <ElencoPerAllenatore
            coaches={coaches}
            sessioni={sessioni}
            aperto={apertoCoach}
            onApri={(id) => setApertoCoach((att) => (uguali(att, id) ? "" : String(id)))}
            statsDi={statsDi}
            casellaDi={casellaDi}
            onCella={apriCella}
            onScheda={apriScheda}
            nomeDi={nomeDi}
          />
        ) : (
          <ElencoPerSessione
            coaches={coaches}
            sessioni={sessioni}
            aperto={apertoEvento}
            onApri={(id) => setApertoEvento((att) => (uguali(att, id) ? "" : String(id)))}
            casellaDi={casellaDi}
            onCella={apriCella}
            nomeDi={nomeDi}
          />
        )
      ) : (
        <GrigliaAllenatori
          coaches={coaches}
          sessioni={sessioni}
          statsDi={statsDi}
          casellaDi={casellaDi}
          onCella={apriCella}
          onScheda={apriScheda}
          nomeDi={nomeDi}
        />
      )}

      <FinestraSegnatura segnatura={segnatura} />
    </HomeShell>
  );
}

// ===========================================================================
// IL SEGNO DI UNA CASELLA — icona Font Awesome, colore dal token
// ===========================================================================
// Verde per la presenza, rosso LEGGIBILE per l'assenza (§1.3: a questo
// gradino il Rosso QPSC non raggiunge AA), testo secondario per «non
// segnato». Sono la coppia già in uso in AttendanceSummary (--yes/--no):
// nessun colore nuovo. Se c'è una nota, accanto al segno sta un'icona
// piccola e il `title` la porta per intero.
function SegnoCasella({ casella }) {
  const voce = casella ? STATO_DI[casella.status] : null;

  if (!voce) {
    return <span className="qpsc-catt-mark qpsc-catt-mark--none">&mdash;</span>;
  }

  const { Icon, etichetta } = voce;
  const nota = casella.note || "";

  return (
    <span
      className={`qpsc-catt-mark qpsc-catt-mark--${casella.status}`}
      title={nota ? `${etichetta} — ${nota}` : etichetta}
    >
      <Icon />
      {nota && (
        <span className="qpsc-catt-mark__note" aria-label="Has a note">
          <FaRegStickyNote />
        </span>
      )}
    </span>
  );
}

/** La pastiglia del tipo evento (TRN/GM), deroga §13.4 (B) come nel modello. */
function PastigliaTipo({ ev, estesa = false }) {
  const training = eAllenamento(ev);

  return (
    <span className={`qpsc-pill qpsc-catt-type--${training ? "training" : "game"}`}>
      {estesa ? ev.type || (training ? "Training" : "Game") : training ? "TRN" : "GM"}
    </span>
  );
}

// ===========================================================================
// LA GRIGLIA — resa ≥768px (doc 07 §8.1: sopra la soglia, tabella)
// ===========================================================================
// Allenatori × sessioni. Calco di `GrigliaPresenze` (AttendanceSummary.jsx)
// senza la colonna di categoria; la nota di una casella si legge nel
// `title` e nella modale. Dalla Fetta 3 il nome è un `<button>` che apre la
// SCHEDA dell'allenatore (`onScheda`), come `onProfilo` nel modello.
function GrigliaAllenatori({ coaches, sessioni, statsDi, casellaDi, onCella, onScheda, nomeDi }) {
  return (
    <div className="qpsc-catt-table-wrap">
      <table className="qpsc-catt-table">
        <thead>
          <tr>
            <th className="qpsc-catt-th qpsc-catt-col-num">#</th>
            <th className="qpsc-catt-th qpsc-catt-col-name">Coach</th>
            <th className="qpsc-catt-th qpsc-catt-col-count" title="Present">
              <FaCheck />
            </th>
            <th className="qpsc-catt-th qpsc-catt-col-count" title="Absent">
              <FaTimes />
            </th>
            <th className="qpsc-catt-th qpsc-catt-col-count">%</th>
            {sessioni.map((ev) => (
              <th
                key={ev.id}
                className="qpsc-catt-th qpsc-catt-col-ev"
                title={`${ev.type || ""} ${ev.opponent ? `vs ${ev.opponent}` : ""} ${
                  ev.date
                } ${ev.time || ""}`.trim()}
              >
                <span className="qpsc-catt-evhead">{etichettaBreve(ev)}</span>
                <PastigliaTipo ev={ev} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {coaches.map((c, indice) => {
            const s = statsDi(c.id);

            return (
              <tr key={c.id}>
                <td className="qpsc-catt-td qpsc-catt-col-num">{indice + 1}</td>
                <td className="qpsc-catt-td qpsc-catt-col-name">
                  <button
                    type="button"
                    className="qpsc-catt-name"
                    onClick={() => onScheda(c.id)}
                  >
                    {nomeDi(c)}
                  </button>
                </td>
                <td className="qpsc-catt-td qpsc-catt-col-count qpsc-catt-count--present">
                  {s.present}
                </td>
                <td className="qpsc-catt-td qpsc-catt-col-count qpsc-catt-count--absent">
                  {s.absent}
                </td>
                <td className="qpsc-catt-td qpsc-catt-col-count">{s.percent}%</td>

                {sessioni.map((ev) => (
                  <td key={ev.id} className="qpsc-catt-td qpsc-catt-col-ev">
                    <button
                      type="button"
                      className="qpsc-catt-cell"
                      onClick={() => onCella(c, ev)}
                      aria-label={`${nomeDi(c)} — ${etichettaBreve(ev)}`}
                    >
                      <SegnoCasella casella={casellaDi(c.id, ev.id)} />
                    </button>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// LE CARD PER ALLENATORE — resa <768px (doc 07 §8.1: sotto la soglia, card)
// ===========================================================================
// Calco di `ElencoPerGiocatore`: testata col nome, sotto i contatori;
// toccando la testata si aprono le sessioni con lo stato di QUELL'allenatore,
// toccando una riga si segna. Il nome sta DENTRO la testata, che è già un
// `<button>`: la scheda (Fetta 3) si apre col comando «View» accanto ai
// contatori — un pulsante non si annida in un pulsante.
function ElencoPerAllenatore({
  coaches,
  sessioni,
  aperto,
  onApri,
  statsDi,
  casellaDi,
  onCella,
  onScheda,
  nomeDi
}) {
  return (
    <div className="qpsc-catt-cards">
      {coaches.map((c) => {
        const s = statsDi(c.id);
        const eAperto = uguali(aperto, c.id);

        return (
          <article key={c.id} className="qpsc-catt-card">
            <button
              type="button"
              className="qpsc-catt-card__head"
              onClick={() => onApri(c.id)}
              aria-expanded={eAperto}
            >
              <span className="qpsc-catt-card__name">{nomeDi(c)}</span>
            </button>

            <div className="qpsc-catt-counters">
              <span className="qpsc-catt-counter qpsc-catt-count--present">
                <FaCheck /> {s.present}
              </span>
              <span className="qpsc-catt-counter qpsc-catt-count--absent">
                <FaTimes /> {s.absent}
              </span>
              <span className="qpsc-catt-counter">{s.percent}%</span>
              <button
                type="button"
                className="qpsc-ghost-btn qpsc-catt-view"
                onClick={() => onScheda(c.id)}
                aria-label={`View ${nomeDi(c)}`}
              >
                View
              </button>
            </div>

            {eAperto && (
              <ul className="qpsc-catt-detail">
                {sessioni.map((ev) => {
                  const casella = casellaDi(c.id, ev.id);

                  return (
                    <li key={ev.id}>
                      <button
                        type="button"
                        className="qpsc-catt-detail__row"
                        onClick={() => onCella(c, ev)}
                      >
                        <span className="qpsc-catt-detail__label">
                          {etichettaBreve(ev)}
                        </span>
                        <PastigliaTipo ev={ev} estesa />
                        <SegnoCasella casella={casella} />
                        {casella?.note && (
                          <span className="qpsc-catt-detail__note">{casella.note}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

// ===========================================================================
// LE CARD PER SESSIONE — «apri l'allenamento di ieri → segna chi c'era»
// ===========================================================================
// Calco di `ElencoPerEvento`: pivot lato client degli stessi dati.
function ElencoPerSessione({ coaches, sessioni, aperto, onApri, casellaDi, onCella, nomeDi }) {
  return (
    <div className="qpsc-catt-cards">
      {sessioni.map((ev) => {
        const eAperto = uguali(aperto, ev.id);

        return (
          <article key={ev.id} className="qpsc-catt-card">
            <button
              type="button"
              className="qpsc-catt-card__head"
              onClick={() => onApri(ev.id)}
              aria-expanded={eAperto}
            >
              <span className="qpsc-catt-card__name">{etichettaBreve(ev)}</span>
              <PastigliaTipo ev={ev} estesa />
            </button>

            {(ev.time || ev.location) && (
              <span className="qpsc-catt-card__meta">
                {[ev.time, ev.location].filter(Boolean).join(" · ")}
              </span>
            )}

            {eAperto && (
              <ul className="qpsc-catt-detail">
                {coaches.map((c) => {
                  const casella = casellaDi(c.id, ev.id);

                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="qpsc-catt-detail__row"
                        onClick={() => onCella(c, ev)}
                      >
                        <span className="qpsc-catt-detail__label">{nomeDi(c)}</span>
                        <SegnoCasella casella={casella} />
                        {casella?.note && (
                          <span className="qpsc-catt-detail__note">{casella.note}</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

// ===========================================================================
// LA MODALE DELLA SEGNATURA (doc 07 §8 «Modali») — stato + nota
// ===========================================================================
// Guscio, titolo e comandi sono le classi condivise di AdminTools.css. Il
// gruppo-scelta è quello §6 del telaio; l'area di testo prende la forma del
// campo di sistema (`qpsc-input`) con la sola classe di pagina per crescere
// in altezza, come `qpsc-clinic__textarea` in ClinicEvents.
function ModaleSegnatura({
  cella,
  stato,
  onStato,
  nota,
  onNota,
  errore,
  onAnnulla,
  onSalva,
  salvataggio
}) {
  return (
    <div className="qpsc-modal-overlay" onClick={onAnnulla}>
      <div
        className="qpsc-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="qpsc-modal__title">Coach attendance</h2>

        <div className="qpsc-modal__body">
          <p className="qpsc-modal__note">
            {cella.nome} &mdash; {cella.evento}
          </p>

          <div className="qpsc-choice-group">
            <span className="qpsc-choice-group__label">Status</span>
            <div className="qpsc-choice-group__options">
              {STATI.map(({ valore: v, etichetta, Icon }) => (
                <button
                  key={v}
                  type="button"
                  className={
                    "qpsc-btn-dark" +
                    (stato === v ? " qpsc-choice-option--selected" : "")
                  }
                  aria-pressed={stato === v}
                  onClick={() => onStato(v)}
                  disabled={salvataggio}
                >
                  <span className="qpsc-catt-option__icon">
                    <Icon />
                  </span>
                  {etichetta}
                </button>
              ))}
            </div>
          </div>

          <label className="qpsc-field qpsc-catt-field">
            <span className="qpsc-field__label">Note (optional)</span>
            <textarea
              className="qpsc-input qpsc-catt-textarea"
              value={nota}
              onChange={(e) => onNota(e.target.value)}
              placeholder="Anything worth remembering about this session."
              maxLength={NOTA_MAX}
              rows={3}
              disabled={salvataggio}
            />
          </label>

          {errore && <p className="qpsc-catt-error">{errore}</p>}
        </div>

        <div className="qpsc-modal__actions">
          <button
            type="button"
            className="qpsc-ghost-btn"
            onClick={onAnnulla}
            disabled={salvataggio}
          >
            Cancel
          </button>
          <button
            type="button"
            className="qpsc-tool-btn"
            onClick={onSalva}
            disabled={salvataggio}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// L'ASSE COACH — un allenatore, le sue sessioni di TUTTE le squadre (Fetta 4)
// ===========================================================================
// È la SCHEDA (CoachAttendanceDetail.jsx) resa SEGNABILE, dentro la pagina:
//   · le letture: il documento (GET /users/:id — nome e tessere) e i record
//     (GET /coach-attendance/coach/:id) INSIEME, per l'id scelto; gli eventi
//     arrivano dal montaggio della pagina, come per l'asse Team. Un guasto di
//     una delle due letture è «la scheda non è disponibile», con il solo 409
//     a parte — mai un elenco vuoto spacciato per «nessuna sessione»;
//   · le squadre: `squadreDelSoggetto` con le opzioni della scheda e le sue
//     DUE CINTURE (status di persona; `assorbeTutte: false`), così l'insieme
//     è quello della griglia (`allenaLaSquadra`, server.js);
//   · le sessioni: gli stessi tre filtri e lo stesso ordine di `sessioni`
//     nell'asse Team, con l'elenco delle squadre al posto della squadra sola;
//   · il totale «X of Y (Z%)»: la formula della griglia, IDENTICA
//     (`present / sessioni.length`), aggregata su tutte le squadre;
//   · la segnatura: la stessa modale, lo stesso POST, la stessa
//     precompilazione, la stessa nota rimandata, la stessa RILETTURA dopo il
//     salvataggio. Ogni sessione è di una squadra che l'allenatore allena,
//     quindi il backend la accetta (`allenaLaSquadra` sul POST).
//
// ⚠️ LA GUARDIA SULL'ID (`coachInCorsoRef`) è quella di `squadraInCorsoRef`:
// la risposta di un allenatore ormai abbandonato si scarta, la rilettura
// dopo un salvataggio sull'allenatore corrente si applica.
//
// ⚠️ LO STATO DELLA MODALE era una copia dichiarata di quello dell'asse Team
// (Fetta 4); dalla Fetta 5 è l'hook `useSegnatura`, chiamato con la
// rilettura di questo asse (`caricaCoach`).

/** L'etichetta di una sessione nella modale e nell'`aria-label`: data breve
 *  e squadra, perché l'asse Coach mescola più squadre. */
const etichettaSessione = (ev) => `${etichettaBreve(ev)} · ${ev.team || ""}`.trim();

function SegnaturaPerAllenatore({ idCoach, annoOperativo, events, compatto }) {
  const [coach, setCoach] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [caricamento, setCaricamento] = useState(false);
  const [errore, setErrore] = useState("");

  const coachInCorsoRef = useRef(null);

  const caricaCoach = useCallback(async () => {
    if (!idCoach) {
      setCoach(null);
      setAttendance([]);
      setErrore("");
      return;
    }

    const id = encodeURIComponent(idCoach);

    try {
      const [documento, presenze] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/coach-attendance/coach/${id}`)
      ]);
      if (coachInCorsoRef.current !== idCoach) return;

      if (!documento || typeof documento !== "object") {
        setCoach(null);
        setAttendance([]);
        setErrore(MSG_COACH);
        return;
      }

      setCoach(documento);
      setAttendance(Array.isArray(presenze) ? presenze : []);
      setErrore("");
    } catch (err) {
      if (coachInCorsoRef.current !== idCoach) return;

      // Il 409 «stagione illeggibile» si distingue sul solo codice; 403,
      // 404 e guasto di rete finiscono nello stesso ramo, senza dettaglio.
      setCoach(null);
      setAttendance([]);
      setErrore(err?.status === 409 ? MSG_STAGIONE : MSG_COACH);
    }
  }, [idCoach]);

  useEffect(() => {
    if (!idCoach) {
      setCoach(null);
      setAttendance([]);
      setErrore("");
      setCaricamento(false);
      return undefined;
    }

    let vivo = true;

    coachInCorsoRef.current = idCoach;
    setCaricamento(true);

    caricaCoach().finally(() => {
      if (vivo) setCaricamento(false);
    });

    return () => {
      vivo = false;
      coachInCorsoRef.current = null;
    };
  }, [idCoach, caricaCoach]);

  const nome = coach ? nomeDi(coach) : "";

  // LE SQUADRE CHE ALLENA — le opzioni e le due cinture della scheda
  // (CoachAttendanceDetail.jsx, `squadre`), riga per riga.
  const squadre = useMemo(() => {
    if (!coach) return [];

    const statusPersona = normalizeRole(coach?.status) || "ACTIVE";
    if (statusPersona !== "ACTIVE") return [];

    const { nomi } = squadreDelSoggetto(coach?.contexts, annoOperativo, {
      ruoli: ["COACH"],
      assorbeTutte: false,
      doppioni: "normalizzati"
    });
    return nomi;
  }, [coach, annoOperativo]);

  // LE SESSIONI — di una QUALSIASI delle sue squadre, già passate, della
  // stagione in corso, dalla più recente (i tre filtri dell'asse Team).
  const sessioni = useMemo(() => {
    if (squadre.length === 0) return [];

    const adesso = Date.now();

    return events
      .filter((ev) => eDiUnaMiaSquadra(ev.team, squadre))
      .filter((ev) => annoDellEvento(ev) === annoOperativo)
      .filter((ev) => evTimeStamp(ev) < adesso)
      .sort((a, b) => evTimeStamp(b) - evTimeStamp(a));
  }, [events, squadre, annoOperativo]);

  // LE CASELLE per evento, `{ status, note }`: la rotta serve già i soli
  // record di questo allenatore; il confronto su `coachId` è la stessa
  // chiave con cui la griglia attribuisce una voce a una persona.
  const caselle = useMemo(() => {
    const perEvento = new Map();

    for (const voce of attendance) {
      if (!uguali(voce.coachId, idCoach)) continue;

      perEvento.set(String(voce.eventId), {
        status: voce.status,
        note: typeof voce.note === "string" ? voce.note : ""
      });
    }

    return perEvento;
  }, [attendance, idCoach]);

  const casellaDi = (eventId) => caselle.get(String(eventId)) || null;

  // IL TOTALE — la formula della griglia, IDENTICA (`statistiche`).
  const totale = useMemo(() => {
    let present = 0;

    for (const ev of sessioni) {
      if (caselle.get(String(ev.id))?.status === "present") present += 1;
    }

    return {
      present,
      percent: sessioni.length ? Math.round((present / sessioni.length) * 100) : 0
    };
  }, [sessioni, caselle]);

  // LA SEGNATURA — l'hook con la rilettura di questo asse (`caricaCoach`):
  // stesso POST, con `coachId` = l'id scelto (id del documento).
  const segnatura = useSegnatura(caricaCoach);

  const apriCella = (ev) =>
    segnatura.apri(
      {
        eventId: ev.id,
        coachId: idCoach,
        nome,
        evento: etichettaSessione(ev)
      },
      casellaDi(ev.id)
    );

  return (
    <>
      {/* IL CONTESTO: chi è, il totale «X of Y (Z%)» in testa (forma della
          scheda), e la riga di stagione. */}
      {idCoach && !caricamento && !errore && (
        <div className="qpsc-catt-context">
          {nome && <span className="qpsc-catt-context__team">{nome}</span>}
          <span className="qpsc-catt-total">
            {totale.present} of {sessioni.length} ({totale.percent}%)
          </span>
          <span className="qpsc-catt-context__meta">
            Season {annoOperativo} &middot; {sessioni.length} past{" "}
            {sessioni.length === 1 ? "session" : "sessions"}, all teams
          </span>
        </div>
      )}

      {errore && <p className="qpsc-catt-error">{errore}</p>}

      {!idCoach ? (
        <p className="qpsc-catt-empty">Select a coach to see their sessions.</p>
      ) : caricamento ? (
        <p className="qpsc-catt-empty">{MSG_ATTESA}</p>
      ) : errore ? null : sessioni.length === 0 ? (
        <p className="qpsc-catt-empty">No past sessions this season.</p>
      ) : compatto ? (
        <ElencoSessioniSegnabili
          sessioni={sessioni}
          casellaDi={casellaDi}
          onCella={apriCella}
        />
      ) : (
        <TabellaSessioni
          sessioni={sessioni}
          casellaDi={casellaDi}
          onCella={apriCella}
        />
      )}

      <FinestraSegnatura segnatura={segnatura} />
    </>
  );
}

// ===========================================================================
// LA TABELLA DELLE SESSIONI — asse Coach, resa ≥768px
// ===========================================================================
// Una riga per sessione (asse trasposto della griglia: le sessioni scendono,
// non corrono di lato, perché qui ce ne sono di più squadre e ogni riga
// deve dire LA SUA). Stesso contenitore e stesse classi di cella della
// griglia; la casella da premere è lo stesso `qpsc-catt-cell`.
function TabellaSessioni({ sessioni, casellaDi, onCella }) {
  return (
    <div className="qpsc-catt-table-wrap">
      <table className="qpsc-catt-table qpsc-catt-table--sessions">
        <thead>
          <tr>
            <th className="qpsc-catt-th qpsc-catt-col-num">#</th>
            <th className="qpsc-catt-th qpsc-catt-col-date">Date</th>
            <th className="qpsc-catt-th qpsc-catt-col-team">Team</th>
            <th className="qpsc-catt-th qpsc-catt-col-type">Type</th>
            <th className="qpsc-catt-th qpsc-catt-col-mark">Status</th>
            <th className="qpsc-catt-th qpsc-catt-col-note">Note</th>
          </tr>
        </thead>
        <tbody>
          {sessioni.map((ev, indice) => {
            const casella = casellaDi(ev.id);

            return (
              <tr key={ev.id}>
                <td className="qpsc-catt-td qpsc-catt-col-num">{indice + 1}</td>
                <td className="qpsc-catt-td qpsc-catt-col-date">
                  <span className="qpsc-catt-session__date">{etichettaBreve(ev)}</span>
                </td>
                <td className="qpsc-catt-td qpsc-catt-col-team">{ev.team}</td>
                <td className="qpsc-catt-td qpsc-catt-col-type">
                  <PastigliaTipo ev={ev} />
                </td>
                <td className="qpsc-catt-td qpsc-catt-col-mark">
                  <button
                    type="button"
                    className="qpsc-catt-cell"
                    onClick={() => onCella(ev)}
                    aria-label={etichettaSessione(ev)}
                  >
                    <SegnoCasella casella={casella} />
                  </button>
                </td>
                <td className="qpsc-catt-td qpsc-catt-col-note">{casella?.note || ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// LE RIGHE DELLE SESSIONI — asse Coach, resa <768px
// ===========================================================================
// Le righe della scheda (`qpsc-catt-session`: data, squadra, pastiglia,
// segno, nota a capo) rese PREMIBILI: ogni riga è un `<button>` che apre la
// modale, difeso dai tag nudi col selettore composto (foglio, nota 4).
function ElencoSessioniSegnabili({ sessioni, casellaDi, onCella }) {
  return (
    <ul className="qpsc-catt-sessions">
      {sessioni.map((ev) => {
        const casella = casellaDi(ev.id);

        return (
          <li key={ev.id}>
            <button
              type="button"
              className="qpsc-catt-session qpsc-catt-session--press"
              onClick={() => onCella(ev)}
              aria-label={etichettaSessione(ev)}
            >
              <span className="qpsc-catt-session__date">{etichettaBreve(ev)}</span>
              <span className="qpsc-catt-session__team">{ev.team}</span>
              <PastigliaTipo ev={ev} />
              <SegnoCasella casella={casella} />
              {casella?.note && (
                <span className="qpsc-catt-detail__note">{casella.note}</span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ===========================================================================
// IL FILTRO DEL REGISTRO — asse REGISTER (Fetta 6)
// ===========================================================================
// Due liste di spunte, «mostra solo» — Teams e Coaches — combinate in AND
// dal registro; lo stato sta nel genitore (sola sessione, v. in testa).
// Tre pezzi di casa, nessuno nuovo:
//   · la RIGA DI COMANDO: il pulsante fantasma §5.3 (`qpsc-ghost-btn`, lo
//     stesso di «View» nelle card) che apre e chiude il pannello con
//     `aria-expanded`, come le testate delle card; accanto, il RIASSUNTO di
//     quanto è attivo («Showing all» / «Showing only 2 teams · 1 coach»),
//     nella resa della riga di contesto (`qpsc-catt-context__meta`); e, solo
//     a filtro attivo, «Clear filters» — lo stesso pulsante fantasma;
//   · il PANNELLO: due gruppi-scelta §6 (`qpsc-choice-group`, quello di
//     «View by» e «Show»), affiancati sopra i 768px e impilati sotto (è il
//     flusso a capo della barra, non due alberi);
//   · le OPZIONI: la spunta di casa (`qpsc-check`, AdminTools.css) — la
//     stessa con cui Team Assignment fa scegliere «una o più squadre» nella
//     finestra delle squadre approvate, e la stessa di «Show family links».
// Il pannello è CHIUSO all'inizio (il registro è a tutta finestra e le
// spunte di un club intero lo spingerebbero in basso); il filtro vale anche
// a pannello chiuso, e il riassunto lo dice. Le voci offerte le decide il
// genitore (`coachOfferti`, `squadreOfferte`).
function FiltroRegistro({
  squadre,
  coach,
  squadreScelte,
  coachScelti,
  onSquadra,
  onCoach,
  onAzzera,
  aperto,
  onApri
}) {
  const nSquadre = squadreScelte.length;
  const nCoach = coachScelti.length;
  const attivi = nSquadre + nCoach;

  const riassunto =
    attivi === 0
      ? "Showing all"
      : "Showing only " +
        [
          nSquadre > 0 ? `${nSquadre} ${nSquadre === 1 ? "team" : "teams"}` : "",
          nCoach > 0 ? `${nCoach} ${nCoach === 1 ? "coach" : "coaches"}` : ""
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <>
      <div className="qpsc-catt-filterbar">
        <button
          type="button"
          className="qpsc-ghost-btn"
          aria-expanded={aperto}
          aria-controls="qpsc-catt-filters"
          onClick={onApri}
        >
          {aperto ? "Hide filters" : "Show filters"}
        </button>

        <span className="qpsc-catt-context__meta">{riassunto}</span>

        {attivi > 0 && (
          <button type="button" className="qpsc-ghost-btn" onClick={onAzzera}>
            Clear filters
          </button>
        )}
      </div>

      {aperto && (
        <div id="qpsc-catt-filters" className="qpsc-catt-filters">
          <div
            className="qpsc-choice-group qpsc-catt-filter"
            role="group"
            aria-labelledby="qpsc-catt-filter-teams"
          >
            <span className="qpsc-choice-group__label" id="qpsc-catt-filter-teams">
              Teams
            </span>
            <div className="qpsc-catt-filter__options">
              {squadre.map((nome) => (
                <label key={nome} className="qpsc-check qpsc-catt-filter__check">
                  <input
                    className="qpsc-check__input"
                    type="checkbox"
                    checked={squadreScelte.includes(nome)}
                    onChange={() => onSquadra(nome)}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  <span>{nome}</span>
                </label>
              ))}
            </div>
          </div>

          <div
            className="qpsc-choice-group qpsc-catt-filter"
            role="group"
            aria-labelledby="qpsc-catt-filter-coaches"
          >
            <span className="qpsc-choice-group__label" id="qpsc-catt-filter-coaches">
              Coaches
            </span>
            <div className="qpsc-catt-filter__options">
              {coach.map((a) => (
                <label key={a.id} className="qpsc-check qpsc-catt-filter__check">
                  <input
                    className="qpsc-check__input"
                    type="checkbox"
                    checked={coachScelti.includes(a.id)}
                    onChange={() => onCoach(a.id)}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  <span>{a.nome}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ===========================================================================
// IL REGISTRO SETTIMANALE — asse REGISTER (Fetta 5)
// ===========================================================================
// Righe = (coach, squadra) con ALMENO UNA sessione nella settimana mostrata;
// colonne = i sette giorni, lunedì per primo. Per navigazione è il
// navigatore di MyCalendar, che vive nella
// barra della pagina (lo stato della settimana sta nel genitore, accanto
// alla squadra e all'allenatore scelti).
//   · le righe: l'elenco del club già letto per l'asse Coach (`allenatori`,
//     con `squadre` — le due cinture di `allenatoriDelClub`); una riga per
//     coppia, tenuta solo se un giorno della settimana ha una sessione di
//     quella squadra (`eDiUnaMiaSquadra`, normalizzato sui due lati);
//   · gli eventi: quelli del montaggio, raggruppati UNA volta per giorno con
//     la chiave locale `YYYY-MM-DD` (pattern di Outlook: `dataEvento` legge
//     i due formati, `new Date(ev.date)` no) e il filtro d'anno operativo
//     dell'asse Team (il backend timbra `season` con l'anno corrente);
//   · le celle: a ogni incrocio (riga, giorno) le sessioni di quella squadra
//     quel giorno. Passata (`evTimeStamp(ev) < adesso`, il metro degli altri
//     assi) → segnabile; futura → visibile ma DISABILITATA (segno «—»);
//     nessuna sessione → cella spenta. Più sessioni lo stesso giorno (raro,
//     il modello non lo vieta: Training e Game) → un segno per sessione,
//     impilati, ciascuno con la propria modale;
//   · i record: GET /coach-attendance/season, UNA lettura al montaggio del
//     registro, rilettura dopo ogni salvataggio (nessuno stato ottimistico);
//     la settimana si naviga senza rileggere nulla;
//   · la segnatura: lo stesso hook, lo stesso POST (`eventId` = id del
//     documento evento, `coachId` = id del documento persona), la stessa
//     precompilazione. Ogni riga è (coach, squadra allenata): il backend
//     accetta (`allenaLaSquadra`).
// Nessun totale: le celle bastano, come nella griglia.
//   · IL FILTRO (Fetta 6): `squadreFiltro` (nomi) e `coachFiltro` (id) dal
//     genitore, «mostra solo», in AND — una riga si vede se la sua squadra è
//     fra quelle scelte (`eDiUnaMiaSquadra`, lo stesso metro normalizzato
//     delle righe) E il suo coach è fra quelli scelti; una lista vuota non
//     restringe. Agisce A VALLE, su `righe` già calcolate: celle, caselle e
//     segnatura non lo vedono. Se la settimana ha righe ma il filtro le
//     toglie tutte, lo si dice con una frase DIVERSA da «No sessions this
//     week.»: la settimana non è vuota, è il filtro che la svuota.
//
// ⚠️ SOTTO I 768px — DEROGA DICHIARATA A §8.1 (la classe vuole card): la
// matrice resta a colonne in scorrimento orizzontale dentro il suo
// contenitore, come Outlook Week; coach e squadra restano fissi (sticky).
// Un registro a card per riga con «i soli giorni con sessione» non è una
// settimana: la decisione è del committente (incarico Fetta 5, §3). La
// resa è UN albero solo con un modificatore di classe per il compatto, non
// due alberi come negli altri assi.
function RegistroSettimanale({
  allenatori,
  annoOperativo,
  events,
  inizioSettimana,
  compatto,
  squadreFiltro,
  coachFiltro
}) {
  const [attendance, setAttendance] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState("");

  // Nessun parametro di rotta, quindi nessuna corsa fra scelte: la sola
  // guardia è «il registro è ancora montato» (vale per la lettura di
  // montaggio e per la rilettura dopo un salvataggio).
  const vivoRef = useRef(true);

  const caricaRegistro = useCallback(async () => {
    try {
      const dati = await api.get("/coach-attendance/season");
      if (!vivoRef.current) return;

      setAttendance(Array.isArray(dati) ? dati : []);
      setErrore("");
    } catch (err) {
      if (!vivoRef.current) return;

      // Il 409 «stagione illeggibile» si distingue sul solo codice; il
      // resto finisce nel ramo generico, senza dettaglio.
      setAttendance([]);
      setErrore(err?.status === 409 ? MSG_STAGIONE : MSG_REGISTRO);
    }
  }, []);

  useEffect(() => {
    vivoRef.current = true;
    setCaricamento(true);

    caricaRegistro().finally(() => {
      if (vivoRef.current) setCaricamento(false);
    });

    return () => {
      vivoRef.current = false;
    };
  }, [caricaRegistro]);

  // I SETTE GIORNI dal lunedì (senza offset: lo spostamento è del
  // navigatore).
  const giorni = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(inizioSettimana);
        d.setDate(inizioSettimana.getDate() + i);
        return d;
      }),
    [inizioSettimana]
  );

  // GLI EVENTI PER GIORNO — una volta per calendario, non per settimana:
  // `Map<localKey, ev[]>` (Outlook, `eventsByDay`), i soli eventi dell'anno
  // operativo con data leggibile, in ordine d'orario.
  const eventiPerGiorno = useMemo(() => {
    const mappa = new Map();

    for (const ev of events) {
      const d = dataEvento(ev);
      if (!d || d.getFullYear() !== annoOperativo) continue;

      const chiave = localKey(d);
      if (!mappa.has(chiave)) mappa.set(chiave, []);
      mappa.get(chiave).push(ev);
    }

    for (const elenco of mappa.values()) {
      elenco.sort((a, b) => evTimeStamp(a) - evTimeStamp(b));
    }

    return mappa;
  }, [events, annoOperativo]);

  // LE RIGHE: per ogni coach, per ogni sua squadra, le sessioni di ciascun
  // giorno (`{ ev, passata }`); la riga resta se un giorno ne ha almeno una.
  // «Passata» si decide qui, una volta per settimana mostrata, col metro
  // degli altri assi (`evTimeStamp(ev) < adesso`).
  const righe = useMemo(() => {
    const adesso = Date.now();
    const esito = [];

    for (const coach of allenatori || []) {
      for (const squadra of coach.squadre || []) {
        const celle = giorni.map((d) =>
          (eventiPerGiorno.get(localKey(d)) || [])
            .filter((ev) => eDiUnaMiaSquadra(ev.team, [squadra]))
            .map((ev) => ({ ev, passata: evTimeStamp(ev) < adesso }))
        );

        if (celle.some((sessioni) => sessioni.length > 0)) {
          esito.push({ coach, squadra, celle });
        }
      }
    }

    return esito;
  }, [allenatori, giorni, eventiPerGiorno]);

  // LE RIGHE MOSTRATE (Fetta 6): `righe` passate al filtro del genitore, in
  // AND. La squadra col metro normalizzato delle righe (`eDiUnaMiaSquadra`),
  // il coach per id con `String`, la chiave di sempre. A filtro vuoto è la
  // stessa lista, e il registro è quello della Fetta 5.
  const righeMostrate = useMemo(() => {
    if (squadreFiltro.length === 0 && coachFiltro.length === 0) return righe;

    const perCoach = new Set(coachFiltro.map(String));

    return righe.filter(
      ({ coach, squadra }) =>
        (squadreFiltro.length === 0 || eDiUnaMiaSquadra(squadra, squadreFiltro)) &&
        (perCoach.size === 0 || perCoach.has(String(coach.id)))
    );
  }, [righe, squadreFiltro, coachFiltro]);

  // LE CASELLE — `{ status, note }` per (coachId, eventId), dai record
  // dell'anno: la stessa attribuzione di `statistiche` (asse Team), senza il
  // conteggio. Gli eventi fuori settimana restano nella mappa e non si
  // guardano: il filtro è la ricerca per evento.
  const caselle = useMemo(() => {
    const perAllenatore = new Map();

    for (const voce of attendance) {
      const chiave = String(voce.coachId);
      if (!perAllenatore.has(chiave)) perAllenatore.set(chiave, new Map());
      perAllenatore.get(chiave).set(String(voce.eventId), {
        status: voce.status,
        note: typeof voce.note === "string" ? voce.note : ""
      });
    }

    return perAllenatore;
  }, [attendance]);

  const casellaDi = (coachId, eventId) =>
    caselle.get(String(coachId))?.get(String(eventId)) || null;

  // LA SEGNATURA — l'hook con la rilettura del registro.
  const segnatura = useSegnatura(caricaRegistro);

  const apriCella = (coach, ev) =>
    segnatura.apri(
      {
        eventId: ev.id,
        coachId: coach.id,
        nome: coach.nome,
        evento: etichettaSessione(ev)
      },
      casellaDi(coach.id, ev.id)
    );

  const inAttesa = caricamento || allenatori === null;

  return (
    <>
      {errore && <p className="qpsc-catt-error">{errore}</p>}

      {inAttesa ? (
        <p className="qpsc-catt-empty">{MSG_ATTESA}</p>
      ) : errore ? null : righe.length === 0 ? (
        <p className="qpsc-catt-empty">No sessions this week.</p>
      ) : righeMostrate.length === 0 ? (
        <p className="qpsc-catt-empty">No coaches match the filter this week.</p>
      ) : (
        <TabellaRegistro
          giorni={giorni}
          righe={righeMostrate}
          casellaDi={casellaDi}
          onCella={apriCella}
          compatto={compatto}
        />
      )}

      <FinestraSegnatura segnatura={segnatura} />
    </>
  );
}

// ===========================================================================
// LA TABELLA DEL REGISTRO — coach, squadra, sette giorni
// ===========================================================================
// Stesso contenitore, stesse classi di testata/cella e stessa casella
// premibile della griglia; le prime due colonne sono FISSE (sticky) col
// pattern delle colonne `#`/`Coach` della griglia, che vuole larghezze note
// (foglio, sezione 9). In una cella-giorno: nessuna sessione → cella spenta
// (`qpsc-catt-reg-off`, un `<td>` vuoto); una o più
// sessioni → un `qpsc-catt-cell` per sessione, impilati, con la pastiglia
// del tipo (qui le colonne sono giorni, non sessioni: il tipo sta nella
// cella) e il segno; futura → `disabled`, segno «—», il `title` lo dice.
function TabellaRegistro({ giorni, righe, casellaDi, onCella, compatto }) {
  return (
    <div className="qpsc-catt-table-wrap">
      <table
        className={
          "qpsc-catt-table qpsc-catt-table--register" +
          (compatto ? " qpsc-catt-table--register-compact" : "")
        }
      >
        <thead>
          <tr>
            <th className="qpsc-catt-th qpsc-catt-reg-coach">Coach</th>
            <th className="qpsc-catt-th qpsc-catt-reg-team">Team</th>
            {giorni.map((d) => (
              <th key={localKey(d)} className="qpsc-catt-th qpsc-catt-reg-day">
                {etichettaGiorno(d)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {righe.map(({ coach, squadra, celle }) => (
            <tr key={`${coach.id}|${squadra}`}>
              <td className="qpsc-catt-td qpsc-catt-reg-coach">{coach.nome}</td>
              <td className="qpsc-catt-td qpsc-catt-reg-team">{squadra}</td>

              {celle.map((sessioni, i) =>
                sessioni.length === 0 ? (
                  <td
                    key={localKey(giorni[i])}
                    className="qpsc-catt-td qpsc-catt-reg-day qpsc-catt-reg-off"
                    title="No session"
                  />
                ) : (
                  <td key={localKey(giorni[i])} className="qpsc-catt-td qpsc-catt-reg-day">
                    <span className="qpsc-catt-reg-stack">
                      {sessioni.map(({ ev, passata }) => (
                        <button
                          key={ev.id}
                          type="button"
                          className="qpsc-catt-cell"
                          disabled={!passata}
                          title={passata ? undefined : "Upcoming session"}
                          onClick={() => onCella(coach, ev)}
                          aria-label={`${coach.nome} — ${etichettaSessione(ev)}${
                            passata ? "" : " (upcoming)"
                          }`}
                        >
                          <PastigliaTipo ev={ev} />
                          <SegnoCasella casella={passata ? casellaDi(coach.id, ev.id) : null} />
                        </button>
                      ))}
                    </span>
                  </td>
                )
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
